import * as React from 'react';
import { Inbox, Lock, RefreshCw, TriangleAlert } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { Button } from './button.js';
import { Skeleton } from './skeleton.js';

/**
 * The five states every screen can be in. They live here, once, because in
 * iteration 1 each screen invented its own and the product read as five
 * products (docs/architecture/09-frontend.md).
 */
function StateShell({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-card border border-dashed border-border',
        'px-6 py-12 text-center',
        className,
      )}
      {...props}
    />
  );
}

export interface EmptyStateProps extends React.ComponentProps<'div'> {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

export function EmptyState({ title, description, icon, action, ...props }: EmptyStateProps) {
  return (
    <StateShell data-slot="empty-state" {...props}>
      <span className="text-faint">{icon ?? <Inbox className="size-6" />}</span>
      <div className="grid gap-1">
        <p className="font-display text-section text-ink">{title}</p>
        {description && <p className="text-secondary text-muted">{description}</p>}
      </div>
      {action}
    </StateShell>
  );
}

export interface ErrorStateProps extends React.ComponentProps<'div'> {
  title?: string;
  description?: string;
  onRetry?: () => void;
}

export function ErrorState({
  title = 'Qualcosa è andato storto',
  description = 'Non è stato possibile caricare i dati. Riprova.',
  onRetry,
  ...props
}: ErrorStateProps) {
  return (
    <StateShell data-slot="error-state" role="alert" className="border-danger/40" {...props}>
      <TriangleAlert className="size-6 text-danger" />
      <div className="grid gap-1">
        <p className="font-display text-section text-ink">{title}</p>
        <p className="text-secondary text-muted">{description}</p>
      </div>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          <RefreshCw />
          Riprova
        </Button>
      )}
    </StateShell>
  );
}

export interface ForbiddenStateProps extends React.ComponentProps<'div'> {
  title?: string;
  description?: string;
}

/**
 * Shown instead of an empty table when the user simply may not see the data —
 * "nessun risultato" for a permission problem is the wrong answer.
 */
export function ForbiddenState({
  title = 'Non hai accesso',
  description = 'Il tuo ruolo non consente di vedere questa sezione.',
  ...props
}: ForbiddenStateProps) {
  return (
    <StateShell data-slot="forbidden-state" {...props}>
      <Lock className="size-6 text-faint" />
      <div className="grid gap-1">
        <p className="font-display text-section text-ink">{title}</p>
        <p className="text-secondary text-muted">{description}</p>
      </div>
    </StateShell>
  );
}

/** Row-shaped placeholder so a loading table keeps the page from jumping. */
export function TableSkeleton({
  rows = 8,
  columns = 5,
  className,
  ...props
}: React.ComponentProps<'div'> & { rows?: number; columns?: number }) {
  return (
    <div data-slot="table-skeleton" className={cn('grid gap-px', className)} aria-busy {...props}>
      {Array.from({ length: rows }, (_, row) => (
        <div
          key={row}
          className="flex h-[var(--row-height)] items-center gap-4 px-[var(--cell-padding-x)]"
        >
          {Array.from({ length: columns }, (_, column) => (
            <Skeleton key={column} className="h-3 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}
